import { memo, useMemo } from 'react';

import {
  DEFAULT_USE_CONTENT_EDITABLE_DATA_TRANSLATIONS_ATTR,
  useContentEditable,
  type UseContentEditableElement,
  UseContentEditableTargetNodeDataAttribute,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/InlineTextEditor/useContentEditable';
import { EDITOR_COMPONENTS_SCHEMA } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import { ComponentTranslationsSchema } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { TranslationKeys } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/types';
import { LocalizationMap } from 'dashboard/models/inquiry-template-version';
import { UiTextComponent, UiTextComponentConfig } from 'lib/interfaces/ui-components';

export type InlineTextEditorProps = {
  componentConfig: UiTextComponentConfig;
  componentPreview: UiTextComponent;
  wrapperElement: HTMLDivElement | null;
  editableNodeDataAttribute?: UseContentEditableTargetNodeDataAttribute;
  editable: boolean;
  children: React.ReactNode;
  onUpdate: (text: string, translationsKey: string) => void;
};

const InlineTextEditor = ({
  componentConfig,
  componentPreview,
  wrapperElement,
  editable,
  children,
  onUpdate,
  editableNodeDataAttribute = DEFAULT_USE_CONTENT_EDITABLE_DATA_TRANSLATIONS_ATTR,
}: InlineTextEditorProps) => {
  const componentTranslationKeys = useMemo(
    () => EDITOR_COMPONENTS_SCHEMA[componentConfig.type]?.properties?.translations ?? [],
    [componentConfig],
  );

  // Note (Evan): We only want to run this once as the translation keys will never change between re-renders
  const visibleTranslations = useMemo(() => {
    if (componentTranslationKeys.length === 0 || !editable || wrapperElement == null) return [];
    return componentTranslationKeys
      .map((translationEntry: ComponentTranslationsSchema) => {
        const { 'client-key': clientKey, 'custom-translations-key': customTranslationsKey } =
          translationEntry;
        if (customTranslationsKey.includes('placeholder')) return false;

        /**
         * Perform a search using XPATH to find the node that contains the attribute text:
         *  - Using wildcard selector due to the specified context within `wrapperElement`
         *    @see {@link https://devhints.io/xpath}
         *  - The returned textNode is the corresponding DOM node
         *    @see {@link https://developer.mozilla.org/en-US/docs/Web/XPath/Introduction_to_using_XPath_in_JavaScript#first_node}
         */

        const xpathEntry = `.//*[contains(text(),'${componentPreview.attributes[clientKey as keyof UiTextComponent['attributes']]}')]`;

        const textNode = document.evaluate(
          xpathEntry,
          wrapperElement,
          null,
          XPathResult.FIRST_ORDERED_NODE_TYPE,
        ).singleNodeValue;

        if (textNode != null && textNode.nodeType === Node.ELEMENT_NODE) {
          const editableNode = textNode as UseContentEditableElement;
          editableNode.setAttribute(
            editableNodeDataAttribute,
            customTranslationsKey as TranslationKeys<LocalizationMap> as string,
          );
          editableNode.setAttribute('key', customTranslationsKey);
          return textNode;
        }
      })
      .filter(Boolean) as Node[];
  }, [componentTranslationKeys, wrapperElement, editable, editableNodeDataAttribute]);

  useContentEditable({
    wrapper: editable ? wrapperElement : null,
    translationNodes: visibleTranslations,
    onUpdate,
    targetNodeDataAttribute: editableNodeDataAttribute,
  });

  return editable ? <div aria-label='inline-text-editor'>{children}</div> : <>{children}</>;
};

export default memo(InlineTextEditor);
